import { capitalize } from 'lodash';

import AddBoxIcon from '@mui/icons-material/AddBox';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import * as K from 'common/constants';
import { Access, Key } from 'common/graphqlTypes';
import ELEMENT, { createTestId } from 'common/element';

import Dropdown from 'components/common/Dropdown/Dropdown';
import Table from 'components/common/Table/Table';

import './ApiKeysSection.scss';
import 'components/common/Button/Button.scss';
import IconButton from 'components/common/IconButton/IconButton';

/**
 * ApiKeysSection represents a single section of the API keys page and displays a list
 * of API keys of a particular type. It allows the user to create (add) a new API key,
 * copy a key's value to the clipboard, and to edit or delete a key.
 *
 * @param {array}    apiKeysList     list of API keys
 * @param {string}   sectionName     title for this list of keys
 * @param {function} handleAddKey    function that adds a key
 * @param {function} handleCopyKey   function that copies a key's value to the clipboard
 * @param {function} handleEditKey   function that renames a key
 * @param {function} handleDeleteKey function that deletes a key
 */

type ApiKeysSectionProps = {
  apiKeysList: [Key];
  sectionName: string;
  handleAddKey: (keyType: string, access: Access) => void;
  handleCopyKey: (apiKey: Key) => void;
  handleEditKey: (apiKey: Key) => void;
  handleDeleteKey: (apiKey: Key) => void;
  testId?: string;
};

function ApiKeysSection({
  apiKeysList,
  sectionName,
  handleAddKey,
  handleCopyKey,
  handleEditKey,
  handleDeleteKey,
  testId,
}: ApiKeysSectionProps): JSX.Element {
  const isLogSection = sectionName === K.LOG_ACCESS_TITLE;
  const keyType: string = isLogSection ? K.LOG_KEY_TYPE : K.CONFIG_KEY_TYPE;

  // plus icon for "Add Key" button
  const leftIcon = <AddBoxIcon fontSize="small" />;
  const sections = [
    [
      { text: 'Add Read Key', clickHandler: (): void => handleAddKey(keyType, Access.READ) },
      { text: 'Add Write Key', clickHandler: (): void => handleAddKey(keyType, Access.WRITE) },
    ],
  ];

  const headers = [
    { content: 'Key Value', field: 'value', width: 500 },
    { content: 'Name', field: 'label' },
    { content: 'Access', field: 'access', width: 90 },
    {
      content: '',
      field: 'actions',
      width: 150,
      isNotSortable: true,
      alignment: K.ALIGNMENT.END,
    },
  ];

  const data = apiKeysList.map((apiKey) => {
    const copyAction = apiKey.hidden ? null : (
      <IconButton
        tooltip="Copy to clipboard"
        onClick={(): void => handleCopyKey(apiKey)}
        testId={ELEMENT.apiKeysCopyToClipboardIcon}
        icon={<FileCopyOutlinedIcon />}
      />
    );
    const actions = (
      <div className="ApiKeysSection__TableIconGroup">
        {copyAction}
        <IconButton
          tooltip="Rename key"
          onClick={(): void => handleEditKey(apiKey)}
          testId={createTestId(testId, 'edit-icon') as ELEMENT}
          icon={<EditIcon />}
        />
        <IconButton
          tooltip="Delete key"
          onClick={(): void => handleDeleteKey(apiKey)}
          testId={createTestId(testId, 'delete-icon') as ELEMENT}
          icon={<DeleteIcon />}
        />
      </div>
    );
    return {
      value: apiKey.value,
      label: apiKey.label,
      access: capitalize(apiKey.access),
      actions,
    };
  });

  const options = {
    isStriped: false,
    isSortable: true,
    tableClass: 'ApiKeysSection__Table',
    headerClass: 'ApiKeysSection__TableHeaderRow',
    rowClass: 'ApiKeysSection__TableRow',
    hoverRow: true,
    hasFooter: false,
  };

  return (
    <div className="ApiKeysSection">
      <div className="ApiKeysSection__Preface">
        <h2 className="SectionHeader" data-test-id={createTestId(testId, 'section-header') as ELEMENT}>
          {sectionName}
        </h2>
        <Dropdown
          sections={sections}
          itemAs="button"
          leftIcon={leftIcon}
          size="med"
          testId={createTestId(testId, 'add-key-button') as ELEMENT}
        >
          Add Key
        </Dropdown>
      </div>
      <div className="ApiKeysSection__TableContainer">
        <Table
          headers={headers}
          data={data}
          options={options}
          testId={createTestId(testId, 'table') as ELEMENT}
          testIdRow={createTestId(testId, 'table-row') as ELEMENT}
          testIdHeader={createTestId(testId, 'table-header') as ELEMENT}
        />
      </div>
    </div>
  );
}

export default ApiKeysSection;
